import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * API para gestión de guias
 * Maneja la comunicación con el backend para obtener y modificar guias
 */

/**
 * Obtener lista de guías con filtros y paginación
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - Parámetros de filtrado y paginación
 * @returns {Promise} Response con lista de guías
 */
const getGuias = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/guias/?';
    const query = [];
    
    // Parámetros de paginación obligatorios
    query.push(`pageSize=${params.pageSize || 10}`);
    query.push(`pageNumber=${params.pageNumber || 1}`);
    
    // Parámetros opcionales de filtrado
    if (params.guia_id) query.push(`guia_id=${params.guia_id}`);
    if (params.estado) query.push(`estado=${encodeURIComponent(params.estado)}`);
    if (params.sub_estado) query.push(`sub_estado=${encodeURIComponent(params.sub_estado)}`);
    if (params.fecha_desde) query.push(`fecha_desde=${params.fecha_desde}`);
    if (params.fecha_hasta) query.push(`fecha_hasta=${params.fecha_hasta}`);
    if (params.fecha) query.push(`fecha=${params.fecha}`);
    if (params.searchGuia) query.push(`searchGuia=${encodeURIComponent(params.searchGuia)}`);
    if (params.searchRuta) query.push(`searchRuta=${encodeURIComponent(params.searchRuta)}`);
    if (params.searchItem) query.push(`searchItem=${encodeURIComponent(params.searchItem)}`);
    if (params.searchGuiaItem) query.push(`searchGuiaItem=${encodeURIComponent(params.searchGuiaItem)}`);
    if (params.cliente_id) query.push(`cliente_id=${params.cliente_id}`);
    if (params.ruta_id) query.push(`ruta_id=${params.ruta_id}`);
    if (params.local_cliente_id) query.push(`local_cliente_id=${params.local_cliente_id}`);
    if (params.tms_id) query.push(`tms_id=${params.tms_id}`);
    if (params.usuario_movil_id) query.push(`usuario_movil_id=${params.usuario_movil_id}`);
    if (params.guias_bulk) query.push(`guias_bulk=${encodeURIComponent(params.guias_bulk)}`);
    
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data || [],
        pagination: response.data.pagination || {},
        message: response.data.message
      };
    }
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      pagination: {}
    };
  } catch (error) {
    console.error('Error al obtener guías:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener guías',
      data: [],
      pagination: {}
    };
  }
};

/**
 * Obtener lista diaria de guías por subestado
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - Parámetros de filtrado
 * @returns {Promise} Response con lista diaria de guías
 */
const getDailyReception = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/guias/reports/daily-reception?';
    const query = [];
    
    // Parámetros obligatorios y opcionales
    // if (params.fecha_inicio) query.push(`fecha_inicio=2025-10-01`);
    // if (params.fecha_fin) query.push(`fecha_fin=2025-10-07`);
    if (params.fecha_inicio) query.push(`fecha_inicio=${params.fecha_inicio}`);
    if (params.fecha_fin) query.push(`fecha_fin=${params.fecha_fin}`);
    if (params.subestado) query.push(`subestado=${encodeURIComponent(params.subestado)}`);
    if (params.estado) query.push(`estado=${encodeURIComponent(params.estado)}`);
    if (params.detalles !== undefined) query.push(`detalles=${params.detalles}`);
    
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data || [],
        estadisticas: response.data.estadisticas || {},
        message: response.data.message
      };
    }
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      estadisticas: {}
    };
  } catch (error) {
    console.error('Error al obtener recepción diaria:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener recepción diaria',
      data: [],
      estadisticas: {}
    };
  }
};

/**
 * Actualizar sub_estados de guías de forma masiva
 * @param {Object} user - Usuario autenticado
 * @param {Array} updates - Array de objetos con guia_id, sub_estado_id y ruta_id opcional
 * @returns {Promise} Response con resultado de la actualización
 */
const updateSubestadosBulk = async (user, updates) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/guias/update-subestados-bulk`;

    const response = await axios.put(endpoint, updates, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Actualización completada exitosamente'
    };
  } catch (error) {
    console.error('Error al actualizar subestados:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar subestados'
    };
  }
};

/**
 * Obtener lista de ítems sin guías (orphans)
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - Parámetros de filtrado por fecha
 * @returns {Promise} Response con lista de ítems orphans
 */
const getOrphanItems = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/guias/items/orphans?';
    const query = [];
    
    // Parámetros de fecha
    if (params.fecha_inicio) query.push(`fecha_inicio=${params.fecha_inicio}`);
    if (params.fecha_fin) query.push(`fecha_fin=${params.fecha_fin}`);
    
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data || [],
        summary: response.data.summary || {},
        message: response.data.message
      };
    }
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      summary: {}
    };
  } catch (error) {
    console.error('Error al obtener ítems orphans:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener ítems orphans',
      data: [],
      summary: {}
    };
  }
};

/**
 * Encontrar guías para ítems orphans
 * @param {Object} user - Usuario autenticado
 * @param {Object} data - Objeto con array de item_ids
 * @returns {Promise} Response con matches encontrados
 */
const findMatchesForItems = async (user, data) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/guias/items/find-matches`;

    const response = await axios.post(endpoint, data, config);
    
    return {
      success: true,
      data: response.data.data || {},
      summary: response.data.summary || {},
      message: response.data.message
    };
  } catch (error) {
    console.error('Error al buscar matches para ítems:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al buscar matches para ítems'
    };
  }
};

/**
 * Reconcialiar ítems sin guías con guías correspondientes
 * @param {Object} user - Usuario autenticado
 * @param {Object} data - Objeto con array de reconciliations
 * @returns {Promise} Response con resultado de la reconciliación
 */
const reconcileItems = async (user, data) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/guias/items/reconcile`;

    const response = await axios.post(endpoint, data, config);
    
    return {
      success: true,
      data: response.data.data || {},
      summary: response.data.summary || {},
      message: response.data.message
    };
  } catch (error) {
    console.error('Error al reconciliar ítems:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al reconciliar ítems'
    };
  }
};

/**
 * Exportar recepción diaria a Excel
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - Parámetros de filtrado (mismos que getDailyReception)
 * @param {string} params.formato - Formato del Excel: 'tipo_manifiesto' o 'informativo'
 * @returns {Promise} Response con el archivo Excel
 */
const exportDailyReceptionExcel = async (user, params = {}) => {
  try {
    const config = {
      ...basicAuthConfig(user),
      responseType: 'blob' // Importante para recibir archivos binarios
    };
    
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/guias/reports/daily-reception-excel?';
    const query = [];
    
    // Parámetros obligatorios y opcionales (mismos que getDailyReception)
    if (params.fecha_inicio) query.push(`fecha_inicio=${params.fecha_inicio}`);
    if (params.fecha_fin) query.push(`fecha_fin=${params.fecha_fin}`);
    if (params.subestado) query.push(`subestado=${encodeURIComponent(params.subestado)}`);
    if (params.estado) query.push(`estado=${encodeURIComponent(params.estado)}`);
    if (params.detalles !== undefined) query.push(`detalles=${params.detalles}`);
    if (params.formato) query.push(`formato=${encodeURIComponent(params.formato)}`);
    
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    
    // Extraer el nombre del archivo del header Content-Disposition
    let filename = 'reporte_diario.xlsx';
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    return {
      success: true,
      data: response.data,
      filename: filename,
      message: 'Excel generado exitosamente'
    };
  } catch (error) {
    console.error('Error al exportar Excel:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al exportar Excel'
    };
  }
};

/**
 * Crear y recepcionar guias de paris
 * @param {Object} user - Usuario autenticado
 * @param {Object} parisData - Datos de las guías a crear
 * @returns {Promise} Response de la creación
 */
const createParis = async (user, parisData, file) => {
  try {
    const config = basicAuthConfig(user);
    const url = `${urlMyspreadApi.replace(/\/$/, '')}/guias/create-paris`;

    const formData = new FormData();
    const filesData = file
    for (let i = 0; i < filesData.length; i++) {
        formData.append(`file[${i}]`, filesData[i])
    }
    formData.append('data', JSON.stringify(parisData));

    const response = await axios.post(url, formData, {
      ...config,
      headers: {
        ...config.headers,
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Proceso comenzado con éxito'
    };
  } catch (error) {
    console.error('Error al procesar:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al procesar'
    };
  }
};

export { 
    createParis,
    getGuias,
    getDailyReception,
    updateSubestadosBulk,
    getOrphanItems,
    findMatchesForItems,
    reconcileItems,
    exportDailyReceptionExcel
};